import { deleteRequest } from '../api/ApiController';
import { UtilProvider } from '../components/UtilContext';
import { databaseStoreRef } from '../redux/DatabaseStore';
import { captureHScrollAndRefreshFromStart, sanitizeTabulatorLayoutAttributes } from './tabulatorProgressiveLoading';
import { uiT } from './uiTranslate';
/**
 * Spoločná logika pre reset masky tabulátora (vymazanie user masky, potom fallback: štandardná maska alebo default).
 * Ak je getFallbackMask zadané a vráti { columns, attributes }, po zmazaní user masky sa aplikuje len ten layout (bez zmeny filtrov/triedenia).
 *
 * state / dispatch / loading dialóg / toast berie z databaseStoreRef a UtilProvider (nastavené v DatabaseStore / UtilContextProvider).
 *
 * @param {Object} opts
 * @param {string} opts.page - názov stránky pre user_mask_filters
 * @param {Object} opts.defaultColumnsVisible - predvolené visibility stĺpcov
 * @param {React.RefObject} opts.ref - ref na Tabulator inštanciu
 * @param {Array} opts.columns - definícia stĺpcov (field, width/minWidth)
 * @param {Function} [opts.getFallbackMask] - () => ({ columns, attributes }) | null – štandardná maska po zmazaní user masky
 * @param {Function} [opts.onAfterReset] – voliteľné; po dokončení (len ak nie je opts.progressiveRefresh)
 * @param {Object} [opts.progressiveRefresh] – { getTable, completedCacheRef, savedHScrollRef?, loadingMessage?, beforeRefresh? }; po resete zachytí H-scroll a volá refresh progressive tabuľky
 */
export async function resetTabulatorMask(opts) {
  const {
    page,
    defaultColumnsVisible,
    ref,
    columns,
    setColumnsVisible,
    getFallbackMask,
    onAfterReset,
    progressiveRefresh,
  } = opts;

  const { state, dispatch } = databaseStoreRef.current;
  const setDialogText = UtilProvider.setDialogText;
  const setVisible = UtilProvider.setVisible;
  const toast = UtilProvider.toast;

  const applyDefaultLayout = (tableEl) => {
    if (typeof tableEl.clearHeaderFilter === 'function') tableEl.clearHeaderFilter();
    if (typeof tableEl.setSort === 'function') tableEl.setSort([]);
    (columns || []).filter((c) => c.field).forEach((col) => {
      if (typeof tableEl.showColumn === 'function') tableEl.showColumn(col.field);
    });
    const table = tableEl.table ?? tableEl;
    if (Array.isArray(columns) && columns.length > 0 && typeof table.setColumns === 'function') {
      try { table.setColumns(columns); } catch (_) {}
    }
    if (typeof table.redraw === 'function') {
      try { table.redraw(true); } catch (_) {}
    }
  };

  const applyFallbackMask = (tableEl, fallback) => {
    const json_columns = fallback.columns && typeof fallback.columns === 'object' ? fallback.columns : null;
    const attributes = sanitizeTabulatorLayoutAttributes(fallback.attributes);
    if (json_columns) {
      setColumnsVisible(json_columns);
      for (const field of Object.keys(json_columns)) {
        if (json_columns[field] === false) tableEl.hideColumn?.(field);
        else tableEl.showColumn?.(field);
      }
    }
    if (attributes.length > 1 && typeof tableEl.setColumnLayout === 'function') {
      try { tableEl.setColumnLayout(attributes); } catch (_) {}
    }
    const table = tableEl.table ?? tableEl;
    if (typeof table?.redraw === 'function') {
      try { table.redraw(true); } catch (_) {}
    }
  };

  try {
    const mask = (state.user_mask_filters || []).find((m) => m.page === page);
    if (mask?.id) {
      setDialogText?.(uiT('Common.ResettingMask'));
      setVisible?.(true);
      try {
        await deleteRequest(`/api/user_mask_filters/${mask.id}`);
        dispatch({
          type: 'load_user_mask_filters',
          newUserMaskFilters: (state.user_mask_filters || []).filter((m) => m.page !== page),
        });
        const fallback = typeof getFallbackMask === 'function' ? getFallbackMask() : null;
        if (fallback && (fallback.columns || (Array.isArray(fallback.attributes) && fallback.attributes.length > 0))) {
          setColumnsVisible(fallback.columns || defaultColumnsVisible);
          if (ref?.current) applyFallbackMask(ref.current, fallback);
        } else {
          setColumnsVisible(defaultColumnsVisible);
          if (ref?.current) applyDefaultLayout(ref.current);
        }
        toast?.current?.show({ severity: 'info', summary: uiT('Common.Confirmed'), life: 3000, detail: uiT('Common.MaskReset') });
      } catch (e) {
        console.error(e);
        toast?.current?.show({ severity: 'error', summary: uiT('Common.Error'), life: 3000, detail: uiT('Common.MaskResetFailed') });
      }
      setVisible?.(false);
    } else {
      const fallback = typeof getFallbackMask === 'function' ? getFallbackMask() : null;
      if (fallback && (fallback.columns || (Array.isArray(fallback.attributes) && fallback.attributes.length > 0))) {
        setColumnsVisible(fallback.columns || defaultColumnsVisible);
        if (ref?.current) applyFallbackMask(ref.current, fallback);
      } else {
        setColumnsVisible(defaultColumnsVisible);
        if (ref?.current) applyDefaultLayout(ref.current);
      }
      toast?.current?.show({ severity: 'info', summary: uiT('Common.Confirmed'), life: 3000, detail: uiT('Common.MaskReset') });
    }
  } finally {
    if (progressiveRefresh) {
      progressiveRefresh.beforeRefresh?.();
      captureHScrollAndRefreshFromStart(
        progressiveRefresh.getTable,
        progressiveRefresh.completedCacheRef,
        progressiveRefresh.savedHScrollRef,
        progressiveRefresh.loadingMessage,
      );
    } else {
      onAfterReset?.();
    }
  }
}
